import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { generateOfferPageRoom, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import styled from 'styled-components'
import { rem } from 'polished'

export const EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT = 36
export const EXPANDED_EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT = 60

const EmbeddedBannerContent = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: ${props => props.$isExpanded ? 'flex-end' : 'center'};
  gap: ${rem(8)};
  width: 100%;
  font-size: ${rem(14)};
  line-height: ${rem(20)};

  > span {
    flex: ${props => props.$isExpanded ? '0 1 auto' : 1};
    min-width: 0;
    overflow: ${props => props.$isExpanded ? 'visible' : 'hidden'};
    text-align: start;
    text-overflow: ${props => props.$isExpanded ? 'clip' : 'ellipsis'};
    white-space: ${props => props.$isExpanded ? 'normal' : 'nowrap'};

    > strong {
      display: ${props => props.$isExpanded ? 'block' : 'inline'};
      margin-bottom: ${props => props.$isExpanded ? rem(4) : 0};
    }
  }

  > a {
    align-self: ${props => props.$isExpanded ? 'flex-end' : 'auto'};
    flex-shrink: 0;
    margin-inline-start: ${props => props.$isExpanded ? rem(48) : 'auto'};
  }
`

const EmbeddedBanner = styled.div<{ $isExpanded: boolean }>`
  box-sizing: border-box;
  height: ${props => rem(props.$isExpanded ? EXPANDED_EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT : EMBEDDED_TRAVELLING_SOLO_BANNER_HEIGHT)};
  padding: ${rem(8)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightContrast};
`

interface Props {
  isExpanded?: boolean
}

function TravellingSoloBanner({ isExpanded = false }: Props) {
  const offerPageDispatch = useOfferPageDispatch()

  const handleViewSoloPackages = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    offerPageDispatch({
      type: OfferPageActions.setRooms,
      rooms: [generateOfferPageRoom({ adults: 1, children: 0, infants: 0 })],
    })
  }, [offerPageDispatch])

  return (
    <EmbeddedBanner $isExpanded={isExpanded}>
      <EmbeddedBannerContent $isExpanded={isExpanded}>
        <span>
          <FormattedMessage
            id="AfPXtLoQtEvBVDa_Ff5B"
            defaultMessage="<strong>Travelling solo?</strong> Unlock special rates"
            description="Compact promotional banner text encouraging solo travellers to view special rates"
            values={{
              strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </span>
        <TextLink underline weight="regular" onClick={handleViewSoloPackages}>
          <FormattedMessage
            id="lPrwzlJE8a8omiMY7tEu"
            defaultMessage="View"
            description="Link text for viewing solo traveller packages from the compact banner"
          />
        </TextLink>
      </EmbeddedBannerContent>
    </EmbeddedBanner>
  )
}

export default React.memo(TravellingSoloBanner)
